import {drawCapacitiesChild} from "./draw-capacities-child";
import {
	FunctionalCapacity, FunctionalCapacityChildrenDiagram,
	FunctionalCapacityGroupDiagram
} from "../../../../../services/back/functional-capacities.service";
import {TranslateService} from "@ngx-translate/core";

// Couleurs
const VIOLET_COLOR = 'rgba(168, 144, 243, 1)';
const VIOLET_COLOR_TRANSPARENT = 'rgba(168, 144, 243, 0.03)';
const ROSE_COLOR = 'rgba(237, 235, 249, 1)';
const WHITE_COLOR = 'rgba(255, 255, 255, 1)';

export function drawCapacities(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	x: number,
	y: number,
	data: FunctionalCapacityGroupDiagram,
	gridSize: number,
	angle: number,
	registerButton: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
	isButtonHovered: (id: string) => boolean,
	translate: TranslateService,
	scale: number,
): void {
	let text = data.name;
	const zoneHeight = data.children.length > 0 ? calculateHeight(data.children, gridSize) : gridSize * 4;
	const zoneWidth = 11 * gridSize;

	ctx.strokeStyle = VIOLET_COLOR;

	//Draw zone
	drawZone(ctx, x, y, zoneHeight, zoneWidth, angle);

	//Draw Title
	drawCapacityTitle(ctx, x, y, data.icon, text, zoneWidth, gridSize, angle, registerButton);
	const buttonX: number = x + zoneWidth - gridSize;
	const buttonY: number = y;
	const buttonWidth: number = gridSize;
	const buttonHeight: number = gridSize;
	const buttonId: string = data.id;
	const isHovered: boolean = isButtonHovered(buttonId)

	drawOptionButton(ctx, buttonX, buttonY, buttonWidth, isHovered, 'violet');
	registerButton(buttonId, "option-button", 'group-option', buttonX, buttonY, buttonWidth, buttonHeight);

	//Draw Child
	if (data.children.length === 0) {
		drawAddCapacities(ctx, translate, data.id, x, y, gridSize, angle, registerButton)
		return
	}

	let baseY: number = y + gridSize * 2
	const baseX: number = x + gridSize
	for (let i = 0; i < data.children.length; i++) {
		drawCapacitiesChild(ctx, data.children[i], baseX, baseY, gridSize, angle, registerButton, isButtonHovered, scale);
		const height: number = calculateChildHeight(data.children[i], gridSize);
		baseY += height;
	}
}

function drawCapacityTitle(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	x: number,
	y: number,
	logo: string | null,
	title: string,
	zoneWidth: number,
	gridSize: number,
	angle: number,
	registerButton: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
): void {
	// Dessin du rectangle
	ctx.beginPath();
	ctx.fillStyle = VIOLET_COLOR;
	ctx.lineWidth = 1;
	ctx.roundRect(x, y, zoneWidth, gridSize, [angle])
	ctx.fill()

	// Dessin du text
	let text = title;
	const maxWidth = gridSize * 9 - (gridSize/3);

	ctx.font = '600 15px proxima-nova';
	const textMetrics = ctx.measureText(text);
	const textWidth = textMetrics.width;

	if (textWidth > maxWidth) {
		let truncatedText = text;
		while (ctx.measureText(truncatedText + '...').width > maxWidth && truncatedText.length > 0) {
			truncatedText = truncatedText.slice(0, -1);
		}
		text = truncatedText + '...';
		registerButton("01", title, 'tool-tip-name', x + gridSize, y + gridSize/4, gridSize * 9, gridSize/2);
	}

	ctx.fillStyle = WHITE_COLOR;
	ctx.textAlign = 'left';
	ctx.textBaseline = 'middle';
	ctx.letterSpacing = "-0.1px"
	ctx.fillText(text, x + 2 + gridSize, y + gridSize / 2);

	// Dessin de l'icône
	const svgPath = "assets/icons/functional-capacities/" + logo + ".svg";
	drawColoredSVG(ctx, svgPath, x, y, gridSize);
}

export function drawOptionButton(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	x: number,
	y: number,
	gridSize: number,
	isHovered: boolean = false,
	buttonType: 'violet' | 'rose'
): void {
	ctx.beginPath();

	if (buttonType === 'violet') {
		if (isHovered) {
			ctx.lineWidth = 1;
			ctx.fillStyle = VIOLET_COLOR;
			ctx.strokeStyle = WHITE_COLOR;
		} else {
			ctx.lineWidth = 2;
			ctx.fillStyle = VIOLET_COLOR;
			ctx.strokeStyle = VIOLET_COLOR;
		}
	} else { // 'rose'
		if (isHovered) {
			ctx.lineWidth = 1;
			ctx.fillStyle = ROSE_COLOR;
			ctx.strokeStyle = VIOLET_COLOR;
		} else {
			ctx.lineWidth = 2;
			ctx.fillStyle = ROSE_COLOR;
			ctx.strokeStyle = ROSE_COLOR;
		}
	}

	ctx.roundRect(x + 5, y + 5, gridSize - 10, gridSize - 10, [6]);
	ctx.fill();
	ctx.stroke()

	ctx.fillStyle = buttonType === 'violet' ? WHITE_COLOR : VIOLET_COLOR;
	const dotRadius = 1.5;
	const centerX: number = x + gridSize / 2;
	const spacing = 5;

	// Dessiner trois points verticalement
	ctx.beginPath();
	ctx.arc(centerX, y + gridSize / 2 - spacing, dotRadius, 0, Math.PI * 2);
	ctx.fill();

	ctx.beginPath();
	ctx.arc(centerX, y + gridSize / 2, dotRadius, 0, Math.PI * 2);
	ctx.fill();

	ctx.beginPath();
	ctx.arc(centerX, y + gridSize / 2 + spacing, dotRadius, 0, Math.PI * 2);
	ctx.fill();
}

//Zone
function drawZone(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	x: number,
	y: number,
	zoneHeight: number,
	cardWidth: number,
	angle: number,
): void {

	ctx.beginPath()
	ctx.fillStyle = VIOLET_COLOR_TRANSPARENT;
	ctx.lineWidth = 1;
	ctx.roundRect(x, y, cardWidth, zoneHeight, [angle])
	ctx.fill()
	ctx.stroke()
}

function drawAddCapacities(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	translate: TranslateService,
	id: string,
	x: number,
	y: number,
	gridSize: number,
	angle: number,
	registerButton: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
): void {

	ctx.beginPath();
	ctx.fillStyle = ROSE_COLOR;
	ctx.strokeStyle = VIOLET_COLOR;
	ctx.lineWidth = 1;
	ctx.roundRect(x + gridSize, y + gridSize*2, 9 * gridSize, gridSize, [8]);
	ctx.fill();
	ctx.stroke();

	ctx.beginPath();
	ctx.lineWidth = 1.5;
	ctx.lineCap = 'round';
	ctx.moveTo((x + gridSize) + gridSize / 2 + .5, (y+ 2*gridSize) + gridSize / 3 + .5);
	ctx.lineTo((x + gridSize) + gridSize / 2 + .5, (y+ 2*gridSize) + (gridSize / 3) * 2 + .5);
	ctx.moveTo((x + gridSize) + gridSize / 3 + .5, (y+ 2*gridSize) + gridSize / 2 + .5);
	ctx.lineTo((x + gridSize) + (gridSize / 3) * 2 + .5, (y+ 2*gridSize) + gridSize / 2 + .5);
	ctx.stroke();

	ctx.beginPath();
	ctx.fillStyle = VIOLET_COLOR
	ctx.font = '500 13px proxima-nova';
	ctx.textAlign = 'left';
	ctx.textBaseline = 'middle';
	ctx.letterSpacing = "-0.1px";
	ctx.fillText(translate.instant('page.diagram.capacity.nodata-button'), x + gridSize * 2, y + (gridSize * 2) + gridSize / 1.85);

	registerButton(id, translate.instant('page.diagram.capacity.nodata-button'), "add-capacity", x + gridSize, y + gridSize*2, 9 * gridSize, gridSize)
}

export function calculateHeight(
	data: FunctionalCapacityChildrenDiagram[],
	gridSize: number,
): number {

	let globalHeight = gridSize * 2;

	for (const element of data) {
		let height = gridSize;
		const size = element.applications.length;

		if (size > 4) {
			height += (Math.ceil(size / 4) * (2 * gridSize)) + gridSize;
		} else {
			height += 3 * gridSize;
		}

		globalHeight += height + gridSize;
	}

	return globalHeight;
}

function calculateChildHeight(
	data: FunctionalCapacityChildrenDiagram,
	gridSize: number,
): number {

	let height = gridSize;
	const size = data.applications.length;
	if (size > 4) {
		height += (Math.ceil(size / 4) * (2 * gridSize)) + gridSize;
	} else {
		height += 3 * gridSize;
	}

	return height + gridSize;
}

function drawColoredSVG(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	svgPath: string,
	x: number,
	y: number,
	gridSize: number,
): void {
	// Charger le SVG en tant que données XML
	fetch(svgPath)
		.then(response => response.text())
		.then(svgText => {
			// Créer un parser DOM pour le SVG
			const parser = new DOMParser();
			const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
			const svgElement = svgDoc.documentElement;

			// Appliquer la couleur à tous les éléments qui ont un attribut fill ou stroke
			const elementsWithFill = svgElement.querySelectorAll('[fill]:not([fill="none"])');
			elementsWithFill.forEach(el => {
				el.setAttribute('fill', WHITE_COLOR);
			});

			const elementsWithStroke = svgElement.querySelectorAll('[stroke]:not([stroke="none"])');
			elementsWithStroke.forEach(el => {
				el.setAttribute('stroke', WHITE_COLOR);
			});

			// Convertir le SVG modifié en une chaîne de caractères
			const serializer = new XMLSerializer();
			const svgString = serializer.serializeToString(svgElement);

			// Créer une URL de données pour le SVG modifié
			const dataUrl = `data:image/svg+xml;base64,${btoa(svgString)}`;

			const width: number = gridSize * 0.6;

			// Charger le SVG modifié dans une image et le dessiner sur le canvas
			const img = new Image();
			img.onload = function() {
				ctx.drawImage(img, x + gridSize * 0.25, y + gridSize * 0.2, width, width);
			};
			img.src = dataUrl;
		})
		.catch(error => {
			console.error("Erreur lors du chargement ou de la modification du SVG:", error);
		});
}
